import { useCallback, useMemo, useState } from 'react';

import { usePersonalisationContext } from '../../context/PersonalisationContext';
import { useLocale } from '../../context/LocaleContext';
import { calculateFinalPriceModifier } from '../../utilities/priceModifier';
import { baseProductConfigurationStepId } from '../../utilities/strings';

import styles from './personalisation-card.module.css';

/**
 * Custom hook for PersonalisationCard logic and className selection.
 * @param {Object} props - Props passed to the card component.
 * @returns {Object} View model for the card component.
 */
export function useViewModel(props) {
  const {
    id,
    slotId,
    imageSrc,
    imageAlt,
    title,
    description,
    availableForSale,
    compareAtPrice,
    secondaryOptionsName = 'Select option',
    isSelected = false,
    secondaryOptions: secondaryOptionsRaw,
    secondaryOptionsLabel,
    onSecondarySelect,
    price,
    disableSecondaryOptions,
    badge,
    helpPage,
    variants: componentGroupVariants = [],
    priceModifierType,
    priceModifierValue,
    componentGroupId: cardComponentGroupId
  } = props;

  const { selections, updateSelections, currentStepId, variants, personalisationSlots } = usePersonalisationContext();
  const { formatPrice } = useLocale();
  const [modalOpen, setModalOpen] = useState(false);
  const secondaryOptions = useMemo(
    () => (disableSecondaryOptions ? [] : secondaryOptionsRaw),
    [disableSecondaryOptions, secondaryOptionsRaw]
  );

  // Calculate the final price after applying price modifier.
  const finalPrice = useMemo(() => {
    const thePrice = price?.amount;
    const theSlot = personalisationSlots.get(slotId);
    const hasOverridePrice = typeof theSlot?.overridePrice === 'number' && !theSlot.isFreeSlot;

    if (theSlot?.isFreeSlot) {
      return {
        ...(price || {}),
        amount: null
      };
    }

    if (hasOverridePrice) {
      return {
        ...(price || {}),
        amount: theSlot.overridePrice
      };
    }

    if (!price?.amount) return price;

    // If base product configuration step, we want to show the price difference between the selected variant and the lowest variant price.
    if (currentStepId === baseProductConfigurationStepId) {
      const variant = variants.get(id);
      const lowestVariantPrice = Math.min(price.amount, variant.product.priceRange?.minVariantPrice?.amount);

      const priceDifference = price.amount - lowestVariantPrice;

      return {
        ...price,
        amount: priceDifference <= 0 ? null : priceDifference
      };
    }

    if (price.amount <= 0 && thePrice <= 0) {
      return {
        ...price,
        amount: null
      };
    }

    // Only apply price modifier if there is a valid one.
    if (!priceModifierType || !priceModifierValue || priceModifierValue === 0) {
      return { ...price, amount: thePrice };
    }

    // Additional check to ensure we have a meaningful price modifier.
    const modifierValue = Number(priceModifierValue);
    if (isNaN(modifierValue) || modifierValue <= 0) {
      return { ...price, amount: thePrice };
    }

    return {
      ...price,
      amount: calculateFinalPriceModifier({
        priceModifierType,
        priceModifierValue,
        variantPrice: thePrice
      })
    };
  }, [price, priceModifierType, priceModifierValue, variants, id, currentStepId, slotId, personalisationSlots]);

  // Get the original price for comparison.
  const originalPrice = useMemo(() => {
    const thePrice = price?.amount;
    if (!price?.amount) return null;
    const theSlot = personalisationSlots.get(slotId);

    if (theSlot?.isFreeSlot || typeof theSlot?.overridePrice === 'number') return null;

    // Only show original price if there's a valid price modifier.
    if (!priceModifierType || !priceModifierValue || priceModifierValue === 0) return null;

    // Additional check to ensure we have a meaningful price modifier.
    const modifierValue = Number(priceModifierValue);
    if (isNaN(modifierValue) || modifierValue <= 0) return null;
    return {
      ...price,
      amount: thePrice
    };
  }, [price, priceModifierType, priceModifierValue, slotId, personalisationSlots]);

  // Ensure price and compareAtPrice are numbers and include currencyCode.
  const compareAtPriceObj =
    compareAtPrice?.amount != null && !isNaN(Number(compareAtPrice.amount))
      ? { amount: Number(compareAtPrice.amount), currencyCode: compareAtPrice.currencyCode || 'GBP' }
      : null;
  const currentSelectionForSlot = selections.slots.find((slot) => slot.id === slotId)?.selected;
  const hasSecondaryOptions = secondaryOptions && secondaryOptions.length > 1;

  // Find the selected secondary option label
  const selectedSecondaryLabel =
    hasSecondaryOptions && currentSelectionForSlot
      ? secondaryOptions.find((opt) => opt.id === currentSelectionForSlot)?.title || secondaryOptionsLabel
      : secondaryOptionsLabel;

  const itemLabel = hasSecondaryOptions ? secondaryOptionsName : title;

  // Handler for card click.
  const openModal = useCallback(() => {
    if (secondaryOptions) {
      setModalOpen(true);
    }
  }, [secondaryOptions]);

  const handleSelection = useCallback(
    (event) => {
      const selectedValue = event.target.value;
      // Check if this item is already selected - if so, deselect it by setting to null.
      const itemToSelect = currentSelectionForSlot === selectedValue ? null : selectedValue;
      updateSelections({
        slotId,
        itemSelected: itemToSelect,
        ...(cardComponentGroupId != null && { componentGroupId: cardComponentGroupId })
      });
    },
    [updateSelections, slotId, currentSelectionForSlot, cardComponentGroupId]
  );

  // Handler for secondary selection.
  const handleSecondarySelect = useCallback(
    ({ optionId }) => {
      setModalOpen(false);
      // Check if this option is already selected - if so, deselect it by setting to null.
      const itemToSelect = currentSelectionForSlot === optionId ? null : optionId;
      updateSelections({
        slotId,
        itemSelected: itemToSelect,
        ...(cardComponentGroupId != null && { componentGroupId: cardComponentGroupId })
      });
    },
    [updateSelections, slotId, setModalOpen, currentSelectionForSlot, cardComponentGroupId]
  );

  // Handler for label clicks to support toggle behavior.
  const handleLabelClick = useCallback(
    (event) => {
      // Prevent the default radio button behavior.
      event.preventDefault();

      // If this card has secondary options, open the modal instead of directly selecting.
      if (hasSecondaryOptions) {
        // Check if this specific card is selected (either as primary or has a secondary option selected)
        const isThisCardSelected =
          currentSelectionForSlot === id ||
          (currentSelectionForSlot && secondaryOptions.some((opt) => opt.id === currentSelectionForSlot));

        if (isThisCardSelected) {
          // If this card is already selected, deselect it.
          updateSelections({
            slotId,
            itemSelected: null,
            ...(cardComponentGroupId != null && { componentGroupId: cardComponentGroupId })
          });
        } else {
          // Otherwise, open the modal to select a secondary option.
          setModalOpen(true);
        }
      } else {
        // For cards without secondary options, toggle selection directly.
        if (currentSelectionForSlot === id) {
          updateSelections({
            slotId,
            itemSelected: null,
            ...(cardComponentGroupId != null && { componentGroupId: cardComponentGroupId })
          });
        } else {
          updateSelections({
            slotId,
            itemSelected: id,
            ...(cardComponentGroupId != null && { componentGroupId: cardComponentGroupId })
          });
        }
      }
    },
    [
      updateSelections,
      slotId,
      currentSelectionForSlot,
      id,
      hasSecondaryOptions,
      setModalOpen,
      secondaryOptions,
      cardComponentGroupId
    ]
  );

  // Handler for helper modal.
  const [helperModalOpen, setHelperModalOpen] = useState(false);

  // Calculate price difference for secondary options.
  const processedSecondaryOptions = useMemo(() => {
    if (!secondaryOptions) {
      return secondaryOptions;
    }

    const theSlot = personalisationSlots.get(slotId);

    return secondaryOptions.map((option) => {
      const hasOverridePrice = typeof theSlot?.overridePrice === 'number' && !theSlot.isFreeSlot;

      if (theSlot?.isFreeSlot) {
        return {
          ...option,
          price: {
            ...option.price,
            amount: null
          },
          originalPrice: null
        };
      }

      if (hasOverridePrice) {
        return {
          ...option,
          price: {
            ...option.price,
            amount: theSlot.overridePrice
          },
          originalPrice: null
        };
      }

      if (!option.price?.amount) {
        return option;
      }

      const optionPriceAmount = option.price.amount;
      let finalPrice = {
        ...option.price,
        amount: optionPriceAmount
      };
      let originalPrice = null;

      // If base product configuration step, calculate price difference.
      if (
        currentStepId === baseProductConfigurationStepId &&
        variants.get(id)?.product.priceRange?.minVariantPrice?.amount
      ) {
        const variant = variants.get(option.id);
        const lowestVariantPrice = Math.min(option.price.amount, variant.product.priceRange.minVariantPrice.amount);
        const priceDifference = option.price.amount - lowestVariantPrice;
        finalPrice.amount = priceDifference <= 0 ? null : priceDifference;
      }

      // Apply price modifier if there's a valid one.
      if (priceModifierType && priceModifierValue && priceModifierValue !== 0) {
        const modifierValue = Number(priceModifierValue);
        if (!isNaN(modifierValue) && modifierValue > 0) {
          // Store the original price before applying modifier.
          originalPrice = {
            ...option.price,
            amount: optionPriceAmount
          };

          // Apply the price modifier to the final price.
          if (finalPrice.amount && finalPrice.amount > 0) {
            finalPrice = {
              ...finalPrice,
              amount: calculateFinalPriceModifier({
                priceModifierType,
                priceModifierValue,
                variantPrice: finalPrice.amount
              })
            };
          }
        }
      }

      return {
        ...option,
        price: finalPrice,
        originalPrice
      };
    });
  }, [
    secondaryOptions,
    currentStepId,
    variants,
    id,
    priceModifierType,
    priceModifierValue,
    personalisationSlots,
    slotId
  ]);

  /**
   * Get the className card inner wrapper.
   * Centers content when there are no secondary options or no image.
   */
  const cardInnerWrapperClassName = `${styles[`personalisation__card-inner-wrapper`]} ${!imageSrc && secondaryOptions.length > 0 ? styles['personalisation__card-inner-wrapper--center'] : ''}`;
  /**
   * Get the className card inner.
   * Centers content when there are no secondary options or no image.
   */
  const cardInnerClassName = `${styles['personalisation__card-inner']} ${!imageSrc ? styles['personalisation__card-inner--center'] : ''}`;

  return {
    id,
    imageSrc,
    imageAlt,
    title,
    description,
    availableForSale,
    isSelected,
    secondaryOptionsName,
    secondaryOptions: processedSecondaryOptions,
    secondaryOptionsLabel,
    onSecondarySelect,
    openModal,
    handleSecondarySelect,
    price: finalPrice,
    originalPrice,
    compareAtPriceObj,
    selectedSecondaryLabel,
    modalOpen,
    setModalOpen,
    hasSecondaryOptions,
    currentSelectionForSlot,
    itemLabel,
    updateSelections,
    slotId,
    handleSelection,
    formatPrice,
    handleLabelClick,
    componentGroupVariants,
    badge,
    helpPage,
    helperModalOpen,
    setHelperModalOpen,
    cardInnerWrapperClassName,
    cardInnerClassName
  };
}
